import { useState, useEffect } from "react";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

interface ChatFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  messageId: string;
  prompt: string;
  response: string;
  preselectedRating?: number;
  onFeedbackSubmitted?: (rating: number) => void;
  assistantType: "projects" | "correspondence" | "spaces";
  conversationId?: string;
  userId?: string;
  spanId?: string;
}

export function ChatFeedbackModal({
  isOpen,
  onClose,
  messageId,
  prompt,
  response,
  preselectedRating = 0,
  onFeedbackSubmitted,
  assistantType,
  conversationId,
  userId,
  spanId,
}: ChatFeedbackModalProps) {
  const { getAccessToken } = useAuthContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rating, setRating] = useState<number>(preselectedRating);
  const [primaryReason, setPrimaryReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [detailedFeedback, setDetailedFeedback] = useState("");

  // Update rating when preselectedRating changes
  useEffect(() => {
    setRating(preselectedRating);
  }, [preselectedRating]);

  const getPrimaryReasonOptions = (rating: number) => {
    if (rating === 1) {
      return [
        { value: "incomplete", label: "Incomplete" },
        { value: "incorrect", label: "Incorrect" },
        { value: "other", label: "Other" },
      ];
    } else if (rating === 5) {
      return [
        { value: "helpful", label: "Helpful" },
        { value: "comprehensive", label: "Comprehensive" },
        { value: "other", label: "Other" },
      ];
    }
    return [];
  };

  const handlePrimaryReasonChange = (value: string) => {
    setPrimaryReason(value);
    if (value !== "other") {
      setCustomReason("");
    }
  };

  const handleClose = () => {
    setPrimaryReason("");
    setCustomReason("");
    setDetailedFeedback("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const feedback = {
        openai_message_id: messageId,
        prompt,
        response,
        rating,
        primary_reason: primaryReason,
        primary_reason_other: primaryReason === "other" ? customReason : "",
        detailed_feedback: detailedFeedback,
        assistant_type: assistantType,
        user_id: userId || "maven-home-anonymous",
        conversation_id: conversationId || null,
        span_id: spanId || null,
      };

      const token = getAccessToken();
      const apiResponse = await fetch(
        "/.netlify/functions/assistantFeedback",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(feedback),
        }
      );

      if (apiResponse.ok) {
        onFeedbackSubmitted?.(rating);
        handleClose();
      } else {
        const result = await apiResponse.json();
        console.error("Feedback submission error:", result.error);
      }
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    if (rating === 0) return false;
    if (primaryReason === "other" && customReason.trim() === "") return false;
    return true;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Feedback –{" "}
            {rating === 1 ? "Not Good" : rating === 5 ? "Good" : ""}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Primary Reason */}
          {rating > 0 && (
            <div>
              <label
                htmlFor="primary_reason"
                className="block text-sm font-medium mb-2"
              >
                Primary Reason{" "}
                <span className="text-xs text-gray-500">(Optional)</span>
              </label>
              <select
                name="primary_reason"
                id="primary_reason"
                value={primaryReason}
                onChange={(e) => handlePrimaryReasonChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-maven-teal-500 focus:border-maven-teal-500"
                disabled={isSubmitting}
              >
                <option value="">Select a reason...</option>
                {getPrimaryReasonOptions(rating).map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom reason input */}
          {primaryReason === "other" && (
            <div>
              <label
                htmlFor="custom_reason"
                className="block text-sm font-medium mb-2"
              >
                Please specify <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="custom_reason"
                id="custom_reason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-maven-teal-500 focus:border-maven-teal-500"
                placeholder="Please describe your reason..."
                required
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Detailed Feedback */}
          {primaryReason && (
            <div>
              <label
                htmlFor="detailed_feedback"
                className="block text-sm font-medium mb-2"
              >
                Detailed Feedback{" "}
                <span className="text-xs text-gray-500">(Optional)</span>
              </label>
              <textarea
                name="detailed_feedback"
                id="detailed_feedback"
                value={detailedFeedback}
                onChange={(e) => setDetailedFeedback(e.target.value)}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-maven-teal-500 focus:border-maven-teal-500"
                placeholder="Please provide specific details about your feedback..."
                disabled={isSubmitting}
              />
            </div>
          )}

          {primaryReason === "other" && (
            <div className="text-xs text-gray-500">* Required</div>
          )}

          <div className="flex justify-end gap-2 pt-2 text-sm font-medium">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid() || isSubmitting}
              className="px-4 py-2 bg-maven-teal-500 text-white rounded-md hover:bg-maven-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              )}
              {isSubmitting
                ? "Submitting..."
                : primaryReason || detailedFeedback
                ? "Submit"
                : "Submit without details"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
